/* eslint-disable react-hooks/set-state-in-effect */
import { useApolloClient } from '@apollo/client';
import { AsyncSelect } from '@sixfold/common-ui';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  CompaniesDropdownListQuery as CompaniesQueryResult,
  CompaniesDropdownListQueryVariables,
  CompanyDropdownQuery as CompanyQueryResult,
  CompanyDropdownQueryVariables,
  getNodes,
} from '../../lib/graphql';
import { Company } from '../entities';
import { companiesDropdownListQuery, companyDropdownQuery } from '../graphql';
import { compareCompany } from '../utils';

export type CompanySearchResult = Omit<Company, 'tags'>;

interface Props {
  inputProps: {
    disabled?: boolean;
    placeholder: string;
  };
  selectedCompany: Pick<CompanySearchResult, 'company_id'> | null;
  onSelectCompany: (selectedCompany: CompanySearchResult | null) => void;
  companyType?: NonNullable<CompaniesDropdownListQueryVariables['type']>;
}

function companyToOption(company: CompanySearchResult) {
  return {
    value: company.company_id,
    label: `${company.company_name} (ID: ${company.company_id}) ${company.is_test === true ? '[Test]' : ''}`.trim(),
  };
}

export const CompaniesDropdownContainer: React.FC<Props> = ({
  inputProps,
  onSelectCompany,
  selectedCompany,
  companyType,
}) => {
  const apolloClient = useApolloClient();
  const [selectedOption, setSelectedOption] = useState<{ value: string; label: string } | null>(null);
  const lastFetchedCompaniesRef = useRef<CompanySearchResult[]>([]);

  const selectedCompanyId = selectedCompany !== null ? selectedCompany.company_id : null;

  useEffect(() => {
    if (selectedCompanyId === null) {
      setSelectedOption(null);
      return;
    }

    let cancelled = false;

    apolloClient
      .query<CompanyQueryResult, CompanyDropdownQueryVariables>({
        query: companyDropdownQuery,
        variables: { companyId: selectedCompanyId },
      })
      .then(({ data }) => {
        if (!cancelled && data.company != null) {
          setSelectedOption(companyToOption(data.company));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSelectedOption(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedCompanyId, apolloClient]);

  const fetchOptions = useCallback(
    async (input: string) => {
      if (input === '') {
        return [];
      }

      const { data } = await apolloClient.query<CompaniesQueryResult, CompaniesDropdownListQueryVariables>({
        query: companiesDropdownListQuery,
        variables: { textSearch: input, type: companyType },
      });

      const companies = getNodes(data.companies);
      lastFetchedCompaniesRef.current = companies;
      return companies.sort(compareCompany).map(companyToOption);
    },
    [apolloClient, companyType],
  );

  return (
    <AsyncSelect
      isClearable
      size="small"
      placeholder={inputProps.placeholder}
      disabled={inputProps.disabled}
      value={selectedOption}
      fetchOptions={fetchOptions}
      onChange={(option) => {
        setSelectedOption(option);

        if (option === null) {
          onSelectCompany(null);
          return;
        }

        const selected = lastFetchedCompaniesRef.current.find((company) => company.company_id === option.value);
        if (selected !== undefined) {
          onSelectCompany(selected);
        } else {
          onSelectCompany(null);
        }
      }}
    />
  );
};
